import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { downloadXlsx } from "@/lib/download";
import { useSortable, SortableTh } from "@/lib/sort";
import { Trash2, ArrowRight, Download, Filter, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function HistoryPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [allUsers, setAllUsers] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/dispensations")
      .then((r) => setList(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const { sortKey, sortDir, toggle, sorted } = useSortable(list, "period_start", "desc");

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta carga?")) return;
    await api.delete(`/dispensations/${id}`);
    toast.success("Eliminada");
    load();
  };

  const openCleanup = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const params = { dry_run: true };
      if (isAdmin && allUsers) params.all_users = true;
      const { data } = await api.post("/dispensations/cleanup-orphan-ghs", null, { params });
      setPreviewData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error calculando preview");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmCleanup = async () => {
    setConfirming(true);
    try {
      const params = {};
      if (isAdmin && allUsers) params.all_users = true;
      const { data } = await api.post("/dispensations/cleanup-orphan-ghs", null, { params });
      toast.success(`Limpieza completada: ${data.total_orphan_no_gh + data.total_only_marca_gh} líneas eliminadas (${data.total_units_removed.toLocaleString("es-ES")} uds).`);
      setPreviewOpen(false);
      setPreviewData(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en la limpieza");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]" data-testid="history-page">
      {/* Hero · Vídeo */}
      <div className="relative mb-10 rounded-2xl overflow-hidden bg-slate-950 shadow-xl border border-slate-200" data-testid="history-hero-video">
        <video
          src="/video/farmaclass-hero.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-auto max-h-[360px] object-cover block"
          aria-hidden="true"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/70 via-slate-950/20 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center px-8 lg:px-12 max-w-2xl pointer-events-none">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-sky-300 mb-2">FarmaClass · Historial</div>
          <h2 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-white leading-tight">
            Cada estudio cuenta una historia.
          </h2>
          <p className="text-sm text-white/70 mt-2 max-w-lg">
            Mira la evolución de tu farmacia mes a mes — desde aquí gestionas todas tus cargas anteriores.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">
            Historial y estudios
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950">
            Cargas anteriores
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="cleanup-orphan-ghs-button"
            onClick={openCleanup}
            className="inline-flex items-center gap-2 bg-white border border-amber-300 text-amber-800 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-amber-50"
            title="Elimina de todos los estudios los CNs huérfanos (sin GH) y los GHs sólo marca"
          >
            <Filter className="h-4 w-4" strokeWidth={1.5} />
            Limpiar GH huérfanos
          </button>
          <button
            data-testid="export-history-button"
            onClick={() => downloadXlsx("/history/export", "farmaclass_historial.xlsx")}
            className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-900 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-slate-50"
          >
            <Download className="h-4 w-4" strokeWidth={1.5} />
            Exportar Excel
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400">Cargando…</div>
      ) : list.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center text-slate-500">
          No hay cargas todavía.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="history-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-[0.15em] text-slate-500">
              <tr>
                <SortableTh sortKey="label" currentKey={sortKey} direction={sortDir} toggle={toggle}>Etiqueta</SortableTh>
                <SortableTh sortKey="period_start" currentKey={sortKey} direction={sortDir} toggle={toggle}>Periodo (inicio)</SortableTh>
                <SortableTh sortKey="period_end" currentKey={sortKey} direction={sortDir} toggle={toggle}>Periodo (fin)</SortableTh>
                <SortableTh sortKey="total_lines" currentKey={sortKey} direction={sortDir} toggle={toggle} align="right">Líneas</SortableTh>
                <SortableTh sortKey="total_units" currentKey={sortKey} direction={sortDir} toggle={toggle} align="right">Unidades</SortableTh>
                <SortableTh sortKey="created_at" currentKey={sortKey} direction={sortDir} toggle={toggle}>Fecha de carga</SortableTh>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {sorted.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4 font-medium">{d.label || d.filename}</td>
                  <td className="p-4 font-mono-num text-slate-600">{d.period_start}</td>
                  <td className="p-4 font-mono-num text-slate-600">{d.period_end}</td>
                  <td className="p-4 text-right font-mono-num">{d.total_lines}</td>
                  <td className="p-4 text-right font-mono-num font-semibold">
                    {(d.total_units || 0).toLocaleString("es-ES")}
                  </td>
                  <td className="p-4 text-slate-500 text-xs font-mono-num">
                    {new Date(d.created_at).toLocaleString("es-ES")}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/dispensations/${d.id}`}
                        data-testid={`view-study-${d.id}`}
                        className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded-full text-xs font-bold transition-colors"
                      >
                        Ver estudio <ArrowRight className="h-3 w-3" />
                      </Link>
                      <button
                        data-testid={`delete-${d.id}`}
                        onClick={() => remove(d.id)}
                        className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={previewOpen} onOpenChange={(o) => { if (!o && !confirming) { setPreviewOpen(false); setPreviewData(null); } }}>
        <DialogContent className="sm:max-w-2xl" data-testid="cleanup-orphan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Limpiar GH huérfanos
            </DialogTitle>
            <DialogDescription>
              Esta acción elimina de TODOS tus estudios los CNs que (a) no están en el catálogo o no tienen GH asignado, y (b) los CNs cuyo GH es 100% marca (sin alternativa genérica). <strong className="text-rose-600">Operación irreversible.</strong>
            </DialogDescription>
          </DialogHeader>

          {previewLoading ? (
            <div className="py-10 flex items-center justify-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Calculando preview…
            </div>
          ) : previewData ? (
            <div className="space-y-3">
              {isAdmin && (
                <label className="flex items-center gap-2 text-xs bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 cursor-pointer hover:bg-violet-100" data-testid="cleanup-all-users-toggle">
                  <input
                    type="checkbox"
                    checked={allUsers}
                    onChange={async (e) => {
                      setAllUsers(e.target.checked);
                      setPreviewData(null);
                      setPreviewLoading(true);
                      try {
                        const params = { dry_run: true };
                        if (e.target.checked) params.all_users = true;
                        const { data } = await api.post("/dispensations/cleanup-orphan-ghs", null, { params });
                        setPreviewData(data);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Error recalculando preview");
                      } finally {
                        setPreviewLoading(false);
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-violet-900 font-semibold">⚡ Modo admin · Aplicar a TODAS las farmacias del sistema</span>
                </label>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">CNs sin GH</div>
                  <div className="text-2xl font-display font-extrabold text-amber-900 mt-1 font-mono-num">
                    {(previewData.total_orphan_no_gh || 0).toLocaleString("es-ES")}
                  </div>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">GH sólo marca</div>
                  <div className="text-2xl font-display font-extrabold text-rose-900 mt-1 font-mono-num">
                    {(previewData.total_only_marca_gh || 0).toLocaleString("es-ES")}
                  </div>
                </div>
                <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold">Unidades quitadas</div>
                  <div className="text-2xl font-display font-extrabold text-slate-900 mt-1 font-mono-num">
                    {(previewData.total_units_removed || 0).toLocaleString("es-ES")}
                  </div>
                </div>
              </div>

              {previewData.studies_affected === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800 text-center">
                  No hay GHs huérfanos. Todos los estudios están limpios.
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0 border-b border-slate-200">
                      <tr>
                        {previewData.all_users && <th className="text-left p-2 font-bold">Farmacia</th>}
                        <th className="text-left p-2 font-bold">Estudio</th>
                        <th className="text-right p-2 font-bold">Antes</th>
                        <th className="text-right p-2 font-bold text-amber-700">Sin GH</th>
                        <th className="text-right p-2 font-bold text-rose-700">Sólo marca</th>
                        <th className="text-right p-2 font-bold">Después</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(previewData.studies || []).map((s) => (
                        <tr key={s.id}>
                          {previewData.all_users && (
                            <td className="p-2 font-medium truncate max-w-[180px] text-violet-700" title={s.pharmacy}>{s.pharmacy}</td>
                          )}
                          <td className="p-2 font-medium truncate max-w-[200px]" title={s.label}>{s.label}</td>
                          <td className="p-2 text-right font-mono-num text-slate-600">{s.lines_before}</td>
                          <td className="p-2 text-right font-mono-num text-amber-700">−{s.removed_orphan_no_gh}</td>
                          <td className="p-2 text-right font-mono-num text-rose-700">−{s.removed_only_marca_gh}</td>
                          <td className="p-2 text-right font-mono-num font-bold">{s.lines_after}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter className="!justify-between gap-2">
            <button
              type="button"
              onClick={() => { setPreviewOpen(false); setPreviewData(null); }}
              disabled={confirming}
              className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              data-testid="cleanup-cancel"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirmCleanup}
              disabled={confirming || previewLoading || !previewData || previewData.studies_affected === 0}
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold px-5 py-2.5 rounded-full"
              data-testid="cleanup-confirm"
            >
              {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Eliminar definitivamente
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
